import { useUserStore } from "@/stores/userStore";
import { checkDomainMatch } from "@/lib/utils";
import { Modal, Card, Badge } from "antd";
import { Building2, Check } from "lucide-react";
import { useLocation, useNavigate } from "react-router";

interface CompanySwitcherModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Route mappings that depend on the invoicing tool.
 * When switching companies, if the user is on a route that belongs to the
 * old invoicing tool, redirect them to the equivalent route for the new tool.
 */
const INVOICING_ROUTE_REDIRECTS: Record<string, Record<string, string>> = {
  QuickBooks: {
    "/netsuite/approvals": "/quickbooks/approvals",
    "/netsuite/vendor-bill-approvals": "/billing/vendor-bill-approvals",
  },
  NetSuite: {
    "/quickbooks/approvals": "/netsuite/approvals",
    "/billing/vendor-bill-approvals": "/netsuite/vendor-bill-approvals",
  },
};

/**
 * CompanySwitcherModal component
 * Displays a modal with company cards for switching between companies
 * Shows parent companies with their subcompanies nested inside
 */
const CompanySwitcherModal = ({ open, onClose }: CompanySwitcherModalProps) => {
  const { user, currentCompany, setCurrentCompany } = useUserStore();
  const navigate = useNavigate();
  const location = useLocation();

  if (!user?.companies) return null;

  /**
   * Handles company selection
   */
  const handleCompanySelect = (companyId: string) => {
    const selectedCompany = user.companies?.find((c) => c.id === companyId);
    if (selectedCompany) {
      setCurrentCompany(selectedCompany);
      onClose();

      // Check if current route needs to be redirected based on the new company's invoicing tool
      const newInvoicingTool = selectedCompany.invoicingAccounting;
      if (newInvoicingTool) {
        const redirectMap = INVOICING_ROUTE_REDIRECTS[newInvoicingTool];
        if (redirectMap) {
          const currentPath = location.pathname;
          const redirectTo = redirectMap[currentPath];
          if (redirectTo) {
            navigate(redirectTo, { replace: true });
          }
        }
      }
    }
  };

  // Filter companies based on domain if user is a client (User role)
  // When testing mode is enabled, show all companies without domain filtering
  const isClient = user?.role === "User";
  const isTestingMode = import.meta.env.VITE_ENABLE_COMPANY_SWITCHER_TESTING === "true";
  const companiesToDisplay =
    isClient && !isTestingMode
      ? user.companies?.filter((c) => c.domain && checkDomainMatch(window.location.hostname, c.domain)) || []
      : user?.companies || [];

  /**
   * Group companies by parent/child relationship
   */
  const groupedCompanies = companiesToDisplay.reduce(
    (acc, company) => {
      // For now, treating all as parent companies
      // TODO: Add parentCompanyId logic when available
      acc.parents.push(company);
      return acc;
    },
    { parents: [] as typeof user.companies, children: {} as Record<string, typeof user.companies> },
  );

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          <span>Switch Company</span>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
      centered
    >
      <div className="grid grid-cols-1 gap-4 mt-4 max-h-[60vh] overflow-y-auto">
        {groupedCompanies.parents.map((company) => (
          <Card
            key={company.id}
            hoverable
            onClick={() => handleCompanySelect(company.id)}
            className={`cursor-pointer transition-all ${
              currentCompany?.id === company.id
                ? "border-2 border-blue-500 shadow-md"
                : "border border-gray-200 hover:border-blue-300"
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {company.domain ? (
                      <>
                        <span className="text-blue-600">{company.domain}</span>
                        <span className="text-gray-500 font-normal ml-2 text-base">({company.companyName})</span>
                      </>
                    ) : (
                      company.companyName
                    )}
                  </h3>
                  {currentCompany?.id === company.id && (
                    <Badge count={<Check className="h-4 w-4 text-white" />} className="bg-blue-500" />
                  )}
                </div>
                <div className="mt-2 space-y-1">
                  {!company.domain && (
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Name:</span> {company.companyName}
                    </div>
                  )}
                  {company.country && (
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Country:</span> {company.country}
                    </div>
                  )}
                  <div className="flex gap-4 text-xs text-gray-500 mt-2">
                    {company.timezone && <span>🕐 {company.timezone}</span>}
                    {company.currency && <span>💰 {company.currency}</span>}
                    {company.language && <span>🌐 {company.language}</span>}
                  </div>
                </div>

                {/* TODO: Add subcompanies when parentCompanyId is available */}
                {/* {groupedCompanies.children[company.id]?.length > 0 && (
                  <div className="mt-4 pl-4 border-l-2 border-gray-200">
                    <div className="text-xs font-medium text-gray-500 mb-2">SUBCOMPANIES</div>
                    <div className="space-y-2">
                      {groupedCompanies.children[company.id].map((subCompany) => (
                        <div
                          key={subCompany.id}
                          className="p-2 rounded hover:bg-gray-50 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCompanySelect(subCompany.id);
                          }}
                        >
                          <div className="text-sm font-medium">{subCompany.companyName}</div>
                          {subCompany.domain && (
                            <div className="text-xs text-gray-500">{subCompany.domain}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )} */}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </Modal>
  );
};

export default CompanySwitcherModal;
